<template>
    <div v-if="showDock" ref="dockRef" class="zs-dock">
        <!-- Workstation location status: this workstation's own location only. -->
        <SidebarLocation v-if="showLocationRow" />

        <!-- Incoming call(s): each shows caller info with accept/decline controls -->
        <div v-for="call in incomingCalls" :key="call.notificationId ?? call.callId" class="zs-dock-row zs-dock-row--incoming">
            <span class="zs-status-dot zs-status-dot--busy" />
            <div class="zs-dock-row__main">
                <div class="zs-dock-row__lbl">{{ call.callerName || 'Unknown' }}</div>
                <div class="zs-dock-row__sub">Incoming · {{ call.callerNumber }}</div>
                <div v-if="incomingCallContext(call)" class="zs-dock-row__sub zs-dock-row__sub--wrap">
                    {{ incomingCallContext(call) }}
                </div>
            </div>
            <button class="zs-incoming-btn zs-incoming-btn--decline" v-tooltip="'Decline'" @click.stop="declineCall(call)">
                <i class="fa fa-phone-hangup" />
            </button>
            <button class="zs-incoming-btn zs-incoming-btn--accept" v-tooltip="'Accept'" @click.stop="acceptCall(call)">
                <i class="fa fa-phone" />
            </button>
        </div>

        <!-- Desk-phone incoming: ringing on the workstation-linked extension. Decline only (no
             pickup) — see docs/talk-workstation-extension-plan.md. Row click is a future-transfer hook. -->
        <div v-for="call in deskIncomingCalls" :key="call.callId" class="zs-dock-row zs-dock-row--incoming" @click="onLiveCallRowClick(call)">
            <span class="zs-status-dot zs-status-dot--busy" />
            <div class="zs-dock-row__main">
                <div class="zs-dock-row__lbl">{{ remoteLabelForLiveCall(call) }}</div>
                <div class="zs-dock-row__sub">Ringing on desk phone · Ext {{ workstationExtension?.extension }}</div>
            </div>
            <button v-if="call.canDecline" class="zs-incoming-btn zs-incoming-btn--decline" v-tooltip="'Decline'" @click.stop="declineDeskCall(call)">
                <i class="fa fa-phone-hangup" />
            </button>
        </div>

        <div v-if="showPhoneRow" ref="phoneRowRef" class="zs-dock-row" :class="{ 'zs-dock-row--active': hasActiveCall }" @click="openPhone()">
            <span class="zs-status-dot" :class="phoneDotClass" />
            <div class="zs-dock-row__main">
                <div class="zs-dock-row__lbl">{{ phoneLabel }}</div>
                <div v-if="phoneSub" class="zs-dock-row__sub">{{ phoneSub }}</div>
            </div>
            <i class="fa-solid fa-phone zs-dock-row__trail" />
        </div>

        <!-- Starred/primary extensions' live calls (dedicated workstation / browser only — a shared
             workstation hides these, see §0a). Read-only: no cap, ringing included, row click is a
             future-transfer hook. -->
        <div v-for="call in starredLiveCalls" :key="call.callId" class="zs-dock-row zs-dock-row--compact" @click="onLiveCallRowClick(call)">
            <i class="fa-regular fa-phone-volume zs-dock-row__lead" />
            <div class="zs-dock-row__main">
                <span class="zs-dock-row__lbl">{{ starredExtensionLabel(call.extensionId) }}</span>
                <span class="zs-dock-row__sub">{{ describeExtensionLiveCallState(call.state) }} · {{ remoteLabelForLiveCall(call) }}</span>
            </div>
        </div>

        <!-- Parking: one single-line row per lot this user can see (location-scoped on a shared
             workstation — see parking-scope.ts), lit up while calls are waiting in it -->
        <div
            v-for="{ lot, indicator, attention } in parkingRows"
            :key="lot.id"
            class="zs-dock-row zs-dock-row--compact zs-dock-row--parking"
            :class="{ 'zs-dock-row--attention': attention }"
            @click="openParkedCalls(lot)"
        >
            <i class="fa-regular fa-square-parking zs-dock-row__lead" />
            <div class="zs-dock-row__main">
                <span class="zs-dock-row__lbl">{{ lot.name }}</span>
                <span class="zs-dock-row__sub">Ext {{ lot.extension }}</span>
            </div>
            <span class="zs-badge" :class="{ 'zs-badge--warn': attention }" v-tooltip="indicator.tooltip">{{ indicator.text }}</span>
        </div>

        <div v-if="showCheckinRow" ref="checkinRowRef" class="zs-dock-row" @click="openCheckins">
            <i class="fa-regular fa-user-clock zs-dock-row__lead" />
            <div class="zs-dock-row__main">
                <div class="zs-dock-row__lbl">{{ unackedCount }} check-in{{ unackedCount === 1 ? '' : 's' }} waiting</div>
                <div class="zs-dock-row__sub">{{ checkinSub }}</div>
            </div>
            <span class="zs-badge zs-badge--warn">{{ unackedCount }}</span>
        </div>

        <div v-if="showNotificationRow" class="zs-dock-row" @click="onNotificationClick">
            <i :class="notificationState === 'blocked' ? 'fa-regular fa-bell-slash' : 'fa-regular fa-bell-exclamation'" class="zs-dock-row__lead" />
            <div class="zs-dock-row__main">
                <div class="zs-dock-row__lbl">Notifications</div>
                <div class="zs-dock-row__sub">{{ notificationSub }}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { ITalkSoftphoneNewCallPushPayload } from '@zyno-platform/shared';

import { formatPhone, handleError, presentOverlay, showToast } from '@zyno-io/vue-foundation';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

import type { ICrmCheckInResponse } from '@/openapi-clients-generated/suite';
import type { IExtensionLiveCallResponse, IParkingLotWithOccupancyResponse } from '@/openapi-clients-generated/talk';

import { GlobalState } from '@/global';
import CheckinPopover from '@/modules/core/qab/c-checkin-icon/f-checkin-popover.vue';
import PhoneDialer from '@/modules/core/qab/c-phone-icon/f-phone-dialer.vue';
import { SSECrmCheckedInChannel } from '@/modules/crm/sse';
import { describeExtensionLiveCallState, partitionDockCalls } from '@/modules/talk/helpers/extension-live-calls';
import { canListParkedCalls, parkingDockIndicator } from '@/modules/talk/helpers/parking-occupancy';
import { isLotInScope, useParkingScope } from '@/modules/talk/helpers/parking-scope';
import { getCallContextEntries, getTransferReasonLabel } from '@/modules/talk/shared/call-context';
import MParkedCalls from '@/modules/talk/shared/components/m-parked-calls.vue';
import { createLiveInvalidation } from '@/modules/talk/shared/composables/use-live-invalidation';
import { extensionLiveCallsMgr } from '@/modules/talk/shared/extension-live-calls.manager';
import { partyLabelForNumber, resolvePartyNumbers } from '@/modules/talk/shared/party-names';
import { softphoneMgr } from '@/modules/talk/shared/softphone.manager';
import { useTalkHelpers, useTalkSoftphoneHelpers, useTalkWorkstationExtension } from '@/modules/talk/shared/store';
import { SSETalkAccessChannel, SSETalkSoftphoneCallChannel } from '@/modules/talk/sse';
import { SuiteCrmCheckInsApi } from '@/openapi-clients-generated/suite';
import { DataCache, type AsyncRef } from '@/shared/data/cache';
import {
    registerAttachmentHandler,
    registerDialHandler,
    registerListenHandler,
    type DialerAttachmentRequest,
    type DialerListenRequest
} from '@/shared/services/dialer.service';
import { ClickToCallExtension } from '@/shared/services/extension-click2call';
import { FCM } from '@/shared/services/fcm';
import { SSEService } from '@/shared/services/sse';
import { useStore } from '@/store';

import SidebarLocation from './c-sidebar-location.vue';

const store = useStore();
const isWorkstation = computed(() => GlobalState.isWorkstation);
// Moved up (was declared near the check-in row) so `showLocationRow` and the parking scope below
// share this one read instead of each re-deriving it from tenantConfiguration.
const workstationLocationId = computed(() => GlobalState.tenantConfiguration.value?.['workstation.locationId']);
// Only for a workstation with a location assigned (config not loaded yet reads as none).
const showLocationRow = computed(() => isWorkstation.value && !!workstationLocationId.value);
const user = computed(() => GlobalState.user.value);

// Mobile devices use the native dialer, so the softphone dock row is dropped
// entirely (not just CSS-hidden) — that lets the dock collapse when empty
// rather than leaving a stray hairline band.
const isMobile = computed(() => store.isMobile || GlobalState.enableMobileAppDesign.value);

// ── Phone dock row ───────────────────────────────────────────────────────────
const dockRef = ref<HTMLElement>();
const phoneRowRef = ref<HTMLElement>();
const { canUseSoftphone } = useTalkSoftphoneHelpers();
const { isSharedWorkstation, blocksPersonalFallback, workstationExtensionId, workstationExtension } = useTalkWorkstationExtension();
const subscribedNumbersRef = DataCache.getRef('talkSubscribedNumbers');
const extensionsById = DataCache.getMapById('talkExtensions');
const hasStarredExtension = computed(
    () => subscribedNumbersRef.value?.some(num => num.objectType === 'extension' && (num.starred || num.primary)) ?? false
);
// Whether this user can place a call from this browser at all. A shared workstation is exclusive:
// only the workstation's own link counts, never the user's personal softphone/starred bindings — see
// docs/talk-workstation-extension-plan.md §0a. Elsewhere (dedicated workstation, browser) this is
// unchanged from before the link existed.
const canDialHere = computed(() => {
    if (isMobile.value) return false;
    // Also fails CLOSED while a workstation session's link hasn't resolved yet — see
    // docs/talk-workstation-extension-plan.md finding #3.
    if (blocksPersonalFallback.value) return !!workstationExtension.value;
    return canUseSoftphone.value || hasStarredExtension.value;
});
const activeCalls = softphoneMgr.getActiveCalls();
const hasActiveCall = computed(() => activeCalls.value.length > 0);
const incomingCalls = softphoneMgr.getIncomingCalls();
const hasIncomingCall = computed(() => incomingCalls.value.length > 0);

// ── Extension live calls: desk-phone rows (linked extension) + starred rows ────────────────────
// Ref-counted, so this only goes live (SSE + polling) while some session actually watches a linked
// or starred/primary extension — see extension-live-calls.manager.ts.
extensionLiveCallsMgr.use();

const softphoneCallIds = computed(() => {
    const ids = new Set<string>();
    for (const call of incomingCalls.value) ids.add(call.callId);
    for (const call of activeCalls.value) if (call.callId) ids.add(call.callId);
    return ids;
});

const liveCallPartition = computed(() =>
    partitionDockCalls({
        calls: extensionLiveCallsMgr.calls.value,
        // Only a SHARED workstation's calls get desk-phone treatment; a dedicated workstation ignores
        // the link entirely, and `workstationExtensionId` is null there regardless of the server value.
        linkedExtensionId: isSharedWorkstation.value ? workstationExtensionId.value : null,
        softphoneCallIds: softphoneCallIds.value
    })
);
const deskIncomingCalls = computed(() => liveCallPartition.value.deskIncoming);
const deskActiveCalls = computed(() => liveCallPartition.value.deskActive);
const starredLiveCalls = computed(() => liveCallPartition.value.starred);
const hasDeskIncomingCall = computed(() => deskIncomingCalls.value.length > 0);
const hasDeskActiveCall = computed(() => deskActiveCalls.value.length > 0);

// Extension live calls carry only the raw remote number — resolve it to a CRM name the same way
// f-phone-dialer.vue does (`resolvePartyNumbers`), so a customer/contact name can show here too,
// not just digits. `remoteLabelForLiveCall` below only ever READS the resolved cache.
watch(
    () => extensionLiveCallsMgr.calls.value.map(c => c.remoteNumber),
    numbers => resolvePartyNumbers(numbers),
    { immediate: true }
);

function remoteLabelForLiveCall(call: IExtensionLiveCallResponse): string {
    const resolved = call.remoteNumber ? partyLabelForNumber(call.remoteNumber) : undefined;
    return resolved || call.remoteName || (call.remoteNumber ? formatPhone(call.remoteNumber) : 'Unknown');
}

function starredExtensionLabel(extensionId: string): string {
    const ext = extensionsById.value[extensionId];
    return ext ? `Ext ${ext.extension} · ${ext.displayName || ext.name}` : 'Ext';
}

async function declineDeskCall(call: IExtensionLiveCallResponse) {
    await extensionLiveCallsMgr.decline(call);
}

// Reserved for a future "transfer to softphone" / "transfer elsewhere" action sheet on a desk or
// starred live-call row (docs/talk-workstation-extension-plan.md §9) — a no-op today.
function onLiveCallRowClick(_call: IExtensionLiveCallResponse) {
    // Intentionally empty.
}

function incomingCallContext(call: ITalkSoftphoneNewCallPushPayload): string | undefined {
    const transferReason = getTransferReasonLabel(call.transferReason);
    if (transferReason) return transferReason;
    const firstContextEntry = getCallContextEntries(call.popupInfo, 1)[0];
    return firstContextEntry ? `${firstContextEntry.label}: ${firstContextEntry.value}` : undefined;
}
// A ringing call already gets its own detail row (with accept/decline), so the
// generic phone row would just duplicate it — drop it while ringing. Keep it
// during call-waiting (an active call + a new incoming) since its "On call"
// state is still meaningful there. Desk-phone ringing on the linked extension
// follows the same rule via its own row above.
const showPhoneRow = computed(
    () => canDialHere.value && !((hasIncomingCall.value || hasDeskIncomingCall.value) && !hasActiveCall.value && !hasDeskActiveCall.value)
);

// Feed softphone call events (incoming/cancelled calls) into the manager. This
// subscription is the source of the incoming-calls queue rendered above; it was
// previously owned by the now-removed c-phone-icon.vue.
SSETalkSoftphoneCallChannel.use(data => softphoneMgr.handleCallEvent(data));

async function acceptCall(call: ITalkSoftphoneNewCallPushPayload) {
    await softphoneMgr.acceptIncomingCall(call);
    openPhone();
}

async function declineCall(call: ITalkSoftphoneNewCallPushPayload) {
    await softphoneMgr.declineIncomingCall(call);
}

// Warn before closing the tab while a call is in progress.
function handleBeforeUnload(e: BeforeUnloadEvent) {
    if (hasActiveCall.value) e.preventDefault();
}

// Wire up the click-to-call browser extension once the phone row is eligible
// (canUseSoftphone / starred extensions load asynchronously). The guard makes
// this run exactly once.
let clickToCallReady = false;
watch(
    showPhoneRow,
    eligible => {
        if (!eligible || clickToCallReady) return;
        clickToCallReady = true;
        ClickToCallExtension.init(number => openPhone(number), hasActiveCall);
    },
    { immediate: true }
);

// Serve in-app dial requests (Talk row actions, contact menus) whenever this user can place a call
// here. Deliberately keyed on capability rather than `showPhoneRow`, which also drops out while a
// call is ringing — a dial request in that window should still open the dialer, not fall through to
// the `tel:` hand-off the service uses when nothing is registered.
let unregisterDialHandler: (() => void) | undefined;
let unregisterAttachmentHandler: (() => void) | undefined;
let unregisterListenHandler: (() => void) | undefined;
watch(
    [canDialHere, canUseSoftphone],
    ([eligible, softphoneEligible]) => {
        if (eligible) {
            unregisterDialHandler ??= registerDialHandler(number => openPhone(number));
            unregisterAttachmentHandler ??= registerAttachmentHandler(request => openPhone(null, request));
        } else {
            unregisterDialHandler?.();
            unregisterDialHandler = undefined;
            unregisterAttachmentHandler?.();
            unregisterAttachmentHandler = undefined;
        }
        if (eligible && softphoneEligible) unregisterListenHandler ??= registerListenHandler(request => openPhone(null, null, request));
        else {
            unregisterListenHandler?.();
            unregisterListenHandler = undefined;
        }
    },
    { immediate: true }
);
onBeforeUnmount(() => {
    unregisterDialHandler?.();
    unregisterAttachmentHandler?.();
    unregisterListenHandler?.();
});

onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    if (clickToCallReady) ClickToCallExtension.cleanup();
});

// Calls and presence arrive over SSE, so the phone's idle reachability mirrors
// the SSE connection: connected → Online, down/reconnecting → Offline.
const isSseConnected = SSEService.isConnected;

const phoneDotClass = computed(() => {
    if (hasActiveCall.value || hasIncomingCall.value || hasDeskActiveCall.value || hasDeskIncomingCall.value) return 'zs-status-dot--busy';
    return isSseConnected.value ? 'zs-status-dot--ok' : 'zs-status-dot--idle';
});

const primaryExtension = computed(() => {
    const subs = subscribedNumbersRef.value ?? [];
    return subs.find(s => s.objectType === 'extension' && (s.primary || s.starred));
});

const phoneLabel = computed(() => {
    // The workstation-linked extension's number wins over the user's personal one — on a shared
    // workstation it's the only phone surface that exists at all (see canDialHere above).
    const ext = primaryExtension.value as { number?: string | null } | undefined;
    const num = workstationExtension.value?.extension ?? ext?.number;
    const state =
        hasActiveCall.value || hasDeskActiveCall.value
            ? 'On call'
            : hasIncomingCall.value || hasDeskIncomingCall.value
              ? 'Incoming'
              : isSseConnected.value
                ? 'Online'
                : 'Offline';
    return num ? `Ext. ${num} · ${state}` : `Phone · ${state}`;
});

// Only surface a sub-line during an active or incoming call; idle shows just the
// label so the row collapses to a single line.
const phoneSub = computed(() => {
    if (hasActiveCall.value) {
        const activeCall = activeCalls.value.find(c => !c.isHeld) || activeCalls.value[0];
        return activeCall?.phoneNumber ?? '';
    }
    if (hasIncomingCall.value) return incomingCalls.value[0]?.callerNumber ?? '';
    // A connected/held/ring-back call on the linked extension shows on the phone row itself, like a
    // softphone call — see docs/talk-workstation-extension-plan.md decisions.
    if (hasDeskActiveCall.value) {
        const call = deskActiveCalls.value[0];
        return call ? `${remoteLabelForLiveCall(call)} · Desk phone` : '';
    }
    return '';
});

const isDialerOpen = ref(false);
// Persisted across opens so click-to-call can push a number into an already-open
// dialer (the dialer watches this ref and dials when it becomes non-null).
const dialedNumberRef = ref<string | null>(null);
// Same shape, for a directed pickup / exact parking retrieval handed off via `registerAttachmentHandler`.
const attachmentRequestRef = ref<DialerAttachmentRequest | null>(null);
const listenRequestRef = ref<DialerListenRequest | null>(null);
function openPhone(
    dialNumber: string | null = null,
    attachment: DialerAttachmentRequest | null = null,
    listenRequest: DialerListenRequest | null = null
) {
    dialedNumberRef.value = dialNumber;
    attachmentRequestRef.value = attachment;
    listenRequestRef.value = listenRequest;
    if (isDialerOpen.value) return;
    isDialerOpen.value = true;
    presentOverlay(
        PhoneDialer,
        { dialedNumberRef, attachmentRequestRef, listenRequestRef },
        {
            // The phone row is the natural anchor, but it is dropped while a call is ringing — an
            // in-app dial request arriving then anchors to the dock itself rather than nothing.
            anchor: { el: (phoneRowRef.value ?? dockRef.value)!, x: 'auto', y: 'auto' },
            onCallback: () => {
                isDialerOpen.value = false;
            }
        }
    );
}

// ── Parking dock rows ────────────────────────────────────────────────────────
// One row per lot this user can see, so a waiting parked call is visible from anywhere in the app.
// The lots come from the shared cache (its Talk-provisioning gate covers tenants without Talk), and
// the cache is only touched once the user is known to hold lot access — for everyone else the list
// endpoint would only answer with an empty, server-filtered array. A shared workstation additionally
// scopes the list to its own location (see parking-scope.ts) — admin pickers reading this same cache
// stay unfiltered.
const { canViewParkingLots } = useTalkHelpers();
const parkingScope = useParkingScope();
const parkingLotsRef = shallowRef<AsyncRef<IParkingLotWithOccupancyResponse[] | undefined>>();
// A legacy-backend lot has no occupancy and no parked-calls list — nothing a row could show or open.
const parkingLots = computed(() =>
    canViewParkingLots.value
        ? (parkingLotsRef.value?.value ?? []).filter(lot => canListParkedCalls(lot) && isLotInScope(lot, parkingScope.value))
        : []
);
const parkingRows = computed(() =>
    parkingLots.value.map(lot => {
        const indicator = parkingDockIndicator(lot);
        return { lot, indicator, attention: indicator.tone === 'attention' };
    })
);

/** Re-read the list in place; the rows keep their last counts until the new ones land. */
async function refreshParkingLots() {
    await parkingLotsRef.value?.backgroundReload?.();
}

// The parking screen's live-refresh protocol: `parking-lot-updated` is the low-latency refresh
// signal, and the list is re-read after a reconnect, on a timer and when the tab is shown again.
// This runs app-wide in every eligible tab, so the safety-net timer is slower than the parking
// screen's, and nothing runs at all for a session without lot access. Gated on lot access only, not
// on `parkingRows.length` — the scope filter can hide every current lot at this workstation's
// location, but a lot can be moved into it later, so the subscription stays live regardless.
const parkingInvalidation = createLiveInvalidation({
    keyFor: event => (event.event === 'parking-lot-updated' ? event.payload.parkingLotId : null),
    onInvalidate: () => refreshParkingLots(),
    onResync: () => refreshParkingLots(),
    intervalMs: 60_000
});
watch(
    canViewParkingLots,
    async eligible => {
        if (!eligible) {
            parkingInvalidation.stop();
            return;
        }
        parkingLotsRef.value ??= DataCache.getRef('talkParkingLots');
        parkingInvalidation.start();
        await parkingLotsRef.value.loadPromise;
        // Eligibility can lapse mid-load (tenant switch, access reload); a stopped instance must not be
        // marked ready, or its next start would skip buffering invalidations behind the fresh snapshot.
        if (!canViewParkingLots.value) return;
        parkingInvalidation.ready();
    },
    { immediate: true }
);
onBeforeUnmount(() => parkingInvalidation.stop());
// A lot grant added or revoked changes which lots this user can see; the server filters the list.
SSETalkAccessChannel.use(() => refreshParkingLots());

const isParkedCallsOpen = ref(false);
async function openParkedCalls(lot: IParkingLotWithOccupancyResponse) {
    if (isParkedCallsOpen.value) return;
    isParkedCallsOpen.value = true;
    try {
        await presentOverlay(MParkedCalls, { lot });
    } finally {
        isParkedCallsOpen.value = false;
    }
}

// ── Check-ins dock row ───────────────────────────────────────────────────────
const checkinRowRef = ref<HTMLElement>();
const checkIns = ref<ICrmCheckInResponse[]>([]);
const unackedCheckIns = computed(() => checkIns.value.filter(c => !c.ackAt));
const unackedCount = computed(() => unackedCheckIns.value.length);
const showCheckinRow = computed(() => unackedCount.value > 0);

const checkinSub = computed(() => {
    const first = unackedCheckIns.value[0];
    if (!first) return '';
    const time = first.createdAt ? new Date(first.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
    return time ? `Oldest ${time}` : '';
});

async function loadCheckIns() {
    try {
        const { data } = await SuiteCrmCheckInsApi.getCrmCheckInsList();
        if (data) checkIns.value = data;
    } catch (err) {
        handleError(err);
    }
}

const isCheckinsOpen = ref(false);
function openCheckins() {
    if (isCheckinsOpen.value) return;
    isCheckinsOpen.value = true;
    presentOverlay(
        CheckinPopover,
        { checkIns },
        {
            anchor: { el: checkinRowRef.value! },
            onCallback: () => {
                isCheckinsOpen.value = false;
            }
        }
    );
}

SSECrmCheckedInChannel.use(event => {
    if (event.action === 'created' && event.locationId === workstationLocationId.value) {
        loadCheckIns();
    } else if (event.action === 'ack') {
        const checkIn = checkIns.value.find(c => c.id === event.id);
        if (checkIn) checkIn.ackAt = new Date().toISOString();
    }
});

onMounted(() => {
    if (user.value) loadCheckIns();
});

// ── Notifications dock row (mirrors c-notification-icon.vue) ─────────────────
const notificationState = ref<'none' | 'prompt' | 'blocked'>('none');
const showNotificationRow = computed(() => notificationState.value !== 'none');
const notificationSub = computed(() => (notificationState.value === 'blocked' ? 'Blocked by browser' : 'Click to enable'));

const isSubscribed = ref(false);
const talkAccessWatch = watch(
    DataCache.getRef('talkAccess'),
    talkAccess =>
        nextTick(() => {
            if (!talkAccess) return;
            talkAccessWatch.stop();
            watch(DataCache.getRef('talkSubscribedNumbers'), subscribedNumbers => {
                isSubscribed.value = !!subscribedNumbers?.some(sn => sn.browserPush);
            });
        }),
    { immediate: true }
);

watch(isSubscribed, refreshNotificationDisplay);

function refreshNotificationDisplay() {
    notificationState.value = 'none';
    if (!isSubscribed.value) return;
    if (GlobalState.isWorkstation) return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') notificationState.value = 'prompt';
    else if (Notification.permission === 'denied') notificationState.value = 'blocked';
}

function onNotificationClick() {
    if (notificationState.value === 'prompt') {
        FCM.registerNotifications()
            .then(didRegister => {
                if (didRegister) {
                    showToast({
                        message: 'Notifications have been successfully enabled in your browser!',
                        durationSecs: 10,
                        position: 'top'
                    });
                }
            })
            .finally(refreshNotificationDisplay);
        return;
    }
    if (notificationState.value === 'blocked') {
        if (navigator.userAgent.includes('Firefox/')) {
            window.open('https://support.mozilla.org/en-US/kb/site-permissions-panel', '_blank');
        } else if (navigator.userAgent.includes('Edg/')) {
            window.open(
                'https://support.microsoft.com/en-us/microsoft-edge/manage-website-notifications-in-microsoft-edge-0c555609-5bf2-479d-a59d-fb30a0b80b2b',
                '_blank'
            );
        } else if (navigator.userAgent.includes('Chrome/')) {
            window.open('https://support.google.com/chrome/answer/3220216?hl=en&co=GENIE.Platform=Desktop', '_blank');
        } else if (navigator.userAgent.includes('Safari/') && /Version\/1[2-9]|[2-9][0-9]/.test(navigator.userAgent)) {
            window.open('https://support.apple.com/guide/safari/manage-website-notifications-sfri40734/mac', '_blank');
        } else {
            window.open('https://google.com/search?q=enable+notifications+in+my+browser', '_blank');
        }
    }
}

const showDock = computed(
    () =>
        showLocationRow.value ||
        showPhoneRow.value ||
        hasIncomingCall.value ||
        hasDeskIncomingCall.value ||
        starredLiveCalls.value.length > 0 ||
        parkingRows.value.length > 0 ||
        showCheckinRow.value ||
        showNotificationRow.value
);
</script>
